import { getBrowserClient } from '@/lib/integrations/supabase/client';
import type { Payment, PaymentInsert } from '@/lib/integrations/supabase/types';
import { authenticatedPost, authenticatedDelete } from '@/lib/core/api/authenticated-client';

/**
 * Get payments for today.
 */
export async function getPaymentsForToday(businessId: string): Promise<Payment[]> {
  const supabase = getBrowserClient();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('business_id', businessId)
    .gte('created_at', today.toISOString())
    .lt('created_at', tomorrow.toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Failed to fetch today payments');
  }

  return data || [];
}

/**
 * Get payments for a date range.
 */
export async function getPaymentsForDateRange(
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<Payment[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('business_id', businessId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Failed to fetch payments');
  }

  return data || [];
}

/**
 * Get recent payments with customer and booking details.
 * Uses a single query with PostgREST joins for optimal performance.
 */
export async function getRecentPayments(
  businessId: string,
  limit: number = 10
): Promise<(Payment & { booking?: { customer?: { name: string } | null } | null })[]> {
  const supabase = getBrowserClient();

  const { data, error } = await supabase
    .from('payments')
    .select(`
      *,
      booking:bookings(
        id,
        customer:customers(name)
      )
    `)
    .eq('business_id', businessId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(error.message || 'Failed to fetch payments');
  }

  return (data ?? []).map((payment: any) => ({
    ...payment,
    booking: payment.booking || null,
  }));
}

/**
 * Get payment statistics for a date range.
 */
export async function getPaymentStats(
  businessId: string,
  startDate: Date,
  endDate: Date
): Promise<{
  total: number;
  byMethod: { cash: number; card: number; transfer: number };
  count: number;
}> {
  const payments = await getPaymentsForDateRange(businessId, startDate, endDate);

  const completedPayments = payments.filter(p => p.status === 'completed');

  const total = completedPayments.reduce((sum, p) => sum + Number(p.amount_aed), 0);

  const byMethod = {
    cash: completedPayments.filter(p => p.method === 'cash').reduce((sum, p) => sum + Number(p.amount_aed), 0),
    card: completedPayments.filter(p => p.method === 'card').reduce((sum, p) => sum + Number(p.amount_aed), 0),
    transfer: completedPayments.filter(p => p.method === 'transfer').reduce((sum, p) => sum + Number(p.amount_aed), 0),
  };

  return {
    total,
    byMethod,
    count: completedPayments.length,
  };
}

/**
 * Get today's earnings summary.
 */
export async function getTodayEarnings(businessId: string): Promise<{
  total: number;
  cash: number;
  card: number;
}> {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const stats = await getPaymentStats(businessId, today, tomorrow);

  return {
    total: stats.total,
    cash: stats.byMethod.cash,
    card: stats.byMethod.card,
  };
}

export interface CreatePaymentData {
  businessId: string;
  bookingId?: string;
  customerId?: string;
  amountAed: number;
  method?: 'cash' | 'card' | 'transfer';
  notes?: string;
}

/**
 * Create a new payment via Route Handler.
 */
export async function createPayment(data: CreatePaymentData): Promise<Payment> {
  return authenticatedPost<Payment>('/api/payments/create', data);
}


/**
 * Delete a payment via Route Handler.
 */
export async function deletePayment(paymentId: string): Promise<void> {
  await authenticatedDelete(`/api/payments/${paymentId}`);
}
